import IdentitiesItem from "@/js/Settings/Identities/Collections/IdentitiesItem";

export { CollectionList as default };

/**
* Items List Collection
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class CollectionList {
  /**
   * Confirmed identities list
   *
   * @public
   *
   * @type {Object.<ItemListEntity}
   *
   * @since 7.0.0
   */
  index = {};

  /**
   * Confirmed identities list
   *
   * @public
   *
   * @type {Array.<ItemListEntity}
   *
   * @since 7.0.0
   */
  items = [];

  /**
   * Number of items in the list
   *
   * @public
   *
   * @type {Number}
   *
   * @since 7.0.0
   */
  count = 0;

  /**
   * Defines Index key id
   *
   * @private
   *
   * @type {Number}
   *
   * @since 7.0.0
   */
  index_id;

  /**
   * Defines outer focus index
   *
   * @public
   *
   * @type {Number}
   *
   * @since 7.0.0
   */
  outer_focus = -1;

  /**
   * Last outer focus selected
   *
   * @public
   *
   * @type {Number}
   */
  last_outer_focus = -1;

  /**
   * Direction used in focus
   *
   * @public
   *
   * @type {String|null}
   */
  outer_focus_direction = null;

  /**
   * What type of last interaction occurred
   * in outer focus, can be click, keyboard actions
   * and others.
   *
   * @public
   *
   * @type {String|null}
   */
  last_outer_focus_interation = null;

  /**
   * Defines inner focus index
   *
   * @public
   *
   * @type {Number}
   *
   * @since 7.0.0
   */
  inner_focus = -1;

  /**
   * Holds last added item to collection
   *
   * @public
   *
   * @type {Object}
   *
   * @since 7.0.0
   */
  last_added;

  /**
   * Holds last added item deleted from collection
   *
   * @public
   *
   * @type {Object}
   *
   * @since 7.0.0
   */
  last_deleted;

  /**
   * Constructor
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {string} index_id
   *
   * @return {void}
   */
  constructor(index_id) {
    this.index_id = index_id;
  }

  /**
   * Returns an Item by id
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Number} id id
   *
   * @return {IdentitiesItem}
   */
  get(id) {
    return this.index[id] || null;
  }

  /**
   * Adds an item to the collection
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {IdentitiesItem} item item to add
   *
   * @return {Boolean}
   */
  add(item) {
    if (this.index[item[this.index_id]]) {
      return false;
    }

    this.index[item[this.index_id]] = item;
    this.items.push(item);
    this.count++;
    this.last_added = item;
    return true;
  }

  /**
   * Adds an unique item to the list, removing everything else from the collection
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ItemListEntity} item item to add
   *
   * @return {Boolean}
   */
  unique(item) {
    if (this.index[item[this.index_id]]) {
      return true;
    }

    this.reset();
    this.add(item);
    return true;
  }

  /**
   * Adds or removes an item from the collection list
   *
   * In order to add an item, the object must me provided
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String|Number}    id
   * @param {Object|undefined} item
   *
   * @return {void}
   */
  toggle(id, item) {
    (this.index[id]) ? this.delete(id) : this.add(item);
  }

  /**
   * Updates an item in the list
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {IdentitiesItem} item item to add
   *
   * @return {void}
   */
  update(item) {
    if (!this.index[item[this.index_id]]) {
      return false;
    }

    this.delete(item[this.index_id]);
    this.add(item);
    return true;
  }

  /**
   * Adds multiple items to the collection
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Object<IdentitiesItem>} items  item to add
   *
   * @return {void}
   */
  addItems(items) {
    for (let item in items) {
      this.add(items[item]);
    }
  }

  /**
   * Add items to list based on a starting and ending
   * range of values
   *
   * @param {Array} list
   * @param {Number} range_start
   * @param {Number} range_end
   *
   * @return {void}
   */
  addItemsRange(list, range_start, range_end){
    const start = Math.min(range_start, range_end);
    const end = Math.max(range_start, range_end);

    for (let index = start; index <= end; index++) {
      this.add(list[index]);
    }
  }

  /**
   * Delets an item from the collection
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String|Number} id item id in the list
   *
   * @return {Boolean}
   */
  delete(id) {
    if (!this.index[id]) {
      return false;
    }

    let item  = this.index[id];
    let index = this.items.indexOf(item);
    this.items.splice(index, 1);
    this.last_deleted = item;
    delete this.index[id];
    this.count--;
    return true;
  }

  /**
   * Pops the last item from the collxtion
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String|Number} id item id in the list
   *
   * @return {void}
   */
  pop() {
    let item = this.items.pop();
    delete this.index[item[this.index_id]];
    this.count--;
    return item;
  }

 /**
   * Returns the first item in the list or null if list is empty
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Object|null}
   */
  getFirst() {
    return this.items[0] || null;
  }

 /**
   * Returns the last added item
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Object|null}
   */
  getLastAdded() {
    return this.last_added;
  }

 /**
   * Returns the last deleted item
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Object|null}
   */
  getLastDeleted() {
    return this.last_deleted;
  }

   /**
   * Returns collection index keys as an array
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Array}
   */
  getIndexKeys() {
    return Object.keys(this.index);
  }

  /**
   * Returns index item from array with base
   * in a value to search
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {String}
   */
  findIndex(search_id){
    return this.items.findIndex(item => item[this.index_id] == search_id);
  }

  /**
   * Checks if item exists in the collection
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} index index id
   *
   * @return {Boolean}
   */
  exists(index) {
    return this.index[index] ? true : false;
  }

  /**
   * Sets outer focus value
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} index_id
   *
   * @return {void}
   */
  setOuterFocusByIndex(index_id) {
    this.setOuterFocus(
      this.findIndex(index_id)
    );
  }

  /**
   * Sets outer focus value
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Number} value
   *
   * @return {void}
   */
  setOuterFocus(value) {
    this.last_outer_focus = this.outer_focus;
    this.outer_focus = value;
  }

  /**
   * Sets outer focus direction
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {String} value
   *
   * @return {void}
   */
  setOuterFocusDirection(value){
    this.outer_focus_direction = value;
  }

  /**
   * Move outer focus up or down
   * depending on the informed direction.
   *
   * @public
   *
   * @param {String} direction
   * @param {Number} move_value default move 1 by 1
   *
   * @returns {Boolean}
   */
  outerFocusMove(direction, move_value = 1){
    if(this.outer_focus_direction){
      move_value = direction !== this.outer_focus_direction ? move_value - 1 : move_value;
    }

    return (direction === 'up' ? this.outerFocusUp(move_value) : this.outerFocusDown(move_value));
  }

  /**
   * Sets the last type of interation in
   * outer focus
   *
   * @public
   *
   * @param {String} value
   */
  setLastOuterFocusInteration(value){
    this.last_outer_focus_interation = value;
  }

  /**
   * Check the last interaction and change the current interaction
   *
   * @param {String} interation_search
   * @param {String} interation_replace
   * @returns {Boolean}
   */
  checkAndChangeLastInteration(interation_search, interation_replace){
    if(this.last_outer_focus_interation == interation_search){
      this.setLastOuterFocusInteration(interation_replace);
      return true;
    }

    return false;
  }

  /**
   * Moves outer focus down by one
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Number} move_value default move 1
   *
   * @return {Boolean}
   */
  outerFocusDown(move_value = 1) {
    if (this.outer_focus >= this.count - 1) {
      return false;
    }

    this.setOuterFocus(this.outer_focus + move_value);
    this.setOuterFocusDirection('down');
    return true;
  }

  /**
   * Moves outer focus up by one
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Number} move_value default move 1
   *
   * @return {Boolean}
   */
  outerFocusUp(move_value = 1) {
    if (this.outer_focus < 0) {
      this.outer_focus = 0;
      return false;
    }

    if (this.outer_focus === 0) {
      return false;
    }

    this.setOuterFocus(this.outer_focus - move_value);
    this.setOuterFocusDirection('up');

    return true;
  }

  /**
   * Resets the collection list to it's initial state
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  reset() {
    this.index               = {};
    this.items               = [];
    this.count               = 0;
    this.inner_focus         = -1;
    this.last_outer_focus    = -1;
    this.outer_focus         = -1;
    this.outer_focus_counter = 0;
    this.last_outer_focus_interation = null;
    this.outer_focus_direction = null;
  }
}